/**
 * Auth service — passwordless email OTP.
 *
 * The frontend never talks to Auth0 directly.
 *  POST /api/auth/otp/start   → backend sends the OTP email via Auth0
 *  POST /api/auth/otp/verify  → backend exchanges OTP for tokens, returns them
 *
 * After a successful verify the service calls GET /api/me so the backend
 * can get-or-create the user row and sync profile data from Auth0 /userinfo.
 *
 * Tokens are persisted via StorageService (Capacitor Preferences on native,
 * localStorage on web). The refresh token keeps the session alive for
 * weeks/months depending on the Auth0 tenant refresh-token TTL setting.
 */

import { AppConfig } from '../config/app-config';
import { MeDto, UpdateMeDto } from '@/app/shared/models';
import { storageService } from '../services/storage.service';
import { concat, defer, distinctUntilChanged, Observable, of, Subject } from 'rxjs';

const BASE = `${AppConfig.apiBaseUrl}/api/auth/otp/email`;

const STORAGE_ACCESS  = 'bm_access_token';
const STORAGE_REFRESH = 'bm_refresh_token';
const STORAGE_EXPIRY  = 'bm_token_expiry';   // unix ms
const STORAGE_USER    = 'bm_user';
const STORAGE_ME      = 'bm_me';             // MeDto from /api/me
const STORAGE_GUEST   = 'bm_guest';          // 'true' when browsing as guest

interface TokenResponse {
  access_token:   string;
  refresh_token?: string;
  expires_in:     number;
  id_token?:      string;
}

interface ApiError extends Error {
  status?: number;
}

function buildApiError(status: number, message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.status = status;
  return error;
}

class AuthService {
  private refreshPromise: Promise<void> | null = null;
  private readonly authStateSubject = new Subject<boolean>();

  /** Emits restored state on subscribe, followed by login and logout changes. */
  readonly authState$: Observable<boolean> = defer(() =>
    concat(of(this.isAuthenticated()), this.authStateSubject),
  ).pipe(distinctUntilChanged());

  // ── Step 1: send OTP via backend ─────────────────────────────────────────

  async sendOtp(email: string): Promise<void> {
    const res = await fetch(`${BASE}/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message ?? 'Failed to send code. Please try again.');
    }
  }

  // ── Step 2: verify OTP via backend, receive tokens ───────────────────────

  async verifyOtp(email: string, otp: string): Promise<void> {
    const res = await fetch(`${BASE}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message ?? 'Invalid code. Please try again.');
    }

    const tokens: TokenResponse = await res.json();
    this.storeTokens(tokens);
    await this.syncMe();
    this.authStateSubject.next(true);
  }

  // ── Token management ─────────────────────────────────────────────────────

  /** Returns a valid access token, refreshing silently when near expiry. */
  async getAccessToken(): Promise<string> {
    const expiry = Number(storageService.getItem(STORAGE_EXPIRY) ?? 0);
    if (Date.now() > expiry - 60_000) {
      await this.refreshTokens();
    }
    const token = storageService.getItem(STORAGE_ACCESS);
    if (!token) throw new Error('Not authenticated.');
    return token;
  }

  isAuthenticated(): boolean {
    return !!storageService.getItem(STORAGE_ACCESS);
  }

  async forceRefresh(): Promise<void> {
    await this.refreshTokens();
  }

  isGuest(): boolean {
    return storageService.getItem(STORAGE_GUEST) === 'true';
  }

  continueAsGuest(): void {
    storageService.setItem(STORAGE_GUEST, 'true');
  }

  getUser(): Record<string, any> | null {
    const raw = storageService.getItem(STORAGE_USER);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  }

  getMe(): MeDto | null {
    const raw = storageService.getItem(STORAGE_ME);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  }

  getAuthSubject(): string | null {
    const subject = this.getMe()?.authSubject ?? this.getUser()?.['sub'];
    return typeof subject === 'string' && subject.trim() ? subject.trim() : null;
  }

  async updateMe(body: UpdateMeDto): Promise<MeDto> {
    const token = await this.getAccessToken();
    if (!token) throw new Error('Not authenticated.');
    const res = await fetch(`${AppConfig.apiBaseUrl}/api/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw buildApiError(res.status, err?.message ?? 'Could not save profile. Please try again.');
    }
    const me: MeDto = await res.json();
    storageService.setItem(STORAGE_ME, JSON.stringify(me));
    return me;
  }

  async updateAvatar(file: File): Promise<MeDto> {
    const token = await this.getAccessToken();
    if (!token) throw new Error('Not authenticated.');
    const form = new FormData();
    form.append('avatar', file);
    const res = await fetch(`${AppConfig.apiBaseUrl}/api/me/avatar`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message ?? 'Could not upload avatar. Please try again.');
    }
    const me: MeDto = await res.json();
    storageService.setItem(STORAGE_ME, JSON.stringify(me));
    return me;
  }

  async deleteAvatar(): Promise<MeDto> {
    const token = await this.getAccessToken();
    if (!token) throw new Error('Not authenticated.');
    const res = await fetch(`${AppConfig.apiBaseUrl}/api/me/avatar`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message ?? 'Could not remove avatar. Please try again.');
    }
    const me: MeDto = await res.json();
    storageService.setItem(STORAGE_ME, JSON.stringify(me));
    return me;
  }

  async deleteAccount(): Promise<void> {
    const token = await this.getAccessToken();
    if (!token) throw new Error('Not authenticated.');
    const res = await fetch(`${AppConfig.apiBaseUrl}/api/me`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message ?? 'Could not delete account. Please try again.');
    }
    this.logout();
  }

  logout(): void {
    storageService.removeItem(STORAGE_ACCESS);
    storageService.removeItem(STORAGE_REFRESH);
    storageService.removeItem(STORAGE_EXPIRY);
    storageService.removeItem(STORAGE_USER);
    storageService.removeItem(STORAGE_ME);
    storageService.removeItem(STORAGE_GUEST);
    this.authStateSubject.next(false);
  }

  // ── Internal ─────────────────────────────────────────────────────────────

  private async syncMe(): Promise<void> {
    try {
      const token = await this.getAccessToken();
      if (!token) return;
      const res = await fetch(`${AppConfig.apiBaseUrl}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const me: MeDto = await res.json();
        storageService.setItem(STORAGE_ME, JSON.stringify(me));
      }
    } catch { /* non-fatal — user is still logged in */ }
  }

  private storeTokens(tokens: TokenResponse): void {
    storageService.setItem(STORAGE_ACCESS,  tokens.access_token);
    storageService.setItem(STORAGE_EXPIRY,  String(Date.now() + tokens.expires_in * 1000));
    storageService.removeItem(STORAGE_GUEST); // clear guest flag on real login
    if (tokens.refresh_token) {
      storageService.setItem(STORAGE_REFRESH, tokens.refresh_token);
    } else {
      // If no refresh_token is returned, the session cannot be silently renewed.
      // Most likely cause: "Allow Offline Access" is disabled on the Auth0 API,
      // or the application does not have the offline_access scope granted.
      console.warn('[AuthService] No refresh_token in token response — session will expire after access token lifetime (~24h). Check Auth0 API "Allow Offline Access" setting.');
    }
    if (tokens.id_token) {
      try {
        const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
        storageService.setItem(STORAGE_USER, JSON.stringify(payload));
      } catch { /* ignore */ }
    }
  }

  private async refreshTokens(): Promise<void> {
    if (this.refreshPromise) {
      await this.refreshPromise;
      return;
    }

    this.refreshPromise = this.performRefreshTokens();

    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefreshTokens(): Promise<void> {
    const refreshToken = storageService.getItem(STORAGE_REFRESH);
    if (!refreshToken) {
      this.logout();
      window.dispatchEvent(new CustomEvent('bm:session-expired'));
      throw new Error('Session expired. Please log in again.');
    }

    const res = await fetch(`${AppConfig.apiBaseUrl}/api/auth/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      this.logout();
      window.dispatchEvent(new CustomEvent('bm:session-expired'));
      throw new Error('Session expired. Please log in again.');
    }

    this.storeTokens(await res.json());
  }
}

export const authService = new AuthService();
